/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { Note } from '../types/types';
import type { DomController } from './DomController';
import type { NoteService } from './NoteService';
import type { DriveService } from './DriveService';
import type { GeminiService } from './GeminiService';
import type { UIService } from './UIService';
import type { EditorService } from './EditorService';
import type { DBService } from './DBService';
import type { AudioService } from './AudioService';
import type { PaneService } from './PaneService';
import type { SettingsService } from './SettingsService';
import type { AuthService } from './AuthService';

type NewNoteAction = 'save' | 'discard' | 'cancel';

/**
 * Manages all modal dialogs like confirmations and selection editors.
 */
export class DialogService {
    private get dom() { return ServiceRegistry.get<DomController>('dom'); }
    private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
    private get driveService() { return ServiceRegistry.get<DriveService>('drive'); }
    private get dbService() { return ServiceRegistry.get<DBService>('dbService'); }
    private get geminiService() { return ServiceRegistry.get<GeminiService>('gemini'); }
    private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
    private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
    private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
    private audioService!: AudioService;
    private paneService!: PaneService;
    private authService!: AuthService;
    
    private polishSelectionRange: Range | null = null;
    private statusManager: StatusManager;

    constructor() {
        // Services are now accessed via ServiceRegistry getters
        // No constructor parameters needed - dependency injection handled by registry
        this.statusManager = new StatusManager(this.dom);
    }
    
    public injectAudioService(audioService: AudioService) { this.audioService = audioService; }
    public injectPaneService(paneService: PaneService) { this.paneService = paneService; }
    public injectAuthService(authService: AuthService) { this.authService = authService; }

    private bindEventListeners(): void {
        this.dom.saveLocalButton.addEventListener('click', () => this.handleSaveLocal());
        this.dom.confirmSaveButton.addEventListener('click', () => this.handleSaveToDrive());
    }

    public async handleNewNoteClick(): Promise<void> {
        if (this.audioService.getIsRecording()) {
            await this.audioService.stopRecording();
        }

        if (this.noteService.isDirty) {
            const action = await this.confirmNewNote();
            if (action === 'cancel') {
                return;
            }
            if (action === 'save') {
                this.editorService.syncActiveContentToModel();
                this.noteService.syncUItoModel(false);
                await this.noteService.saveCurrentNote();
            }
            // If action is 'discard', we proceed without saving.
        }

        await this.noteService.createNewNote();
        this.editorService.resetHistory('polishedNote');
        this.editorService.resetHistory('translatedNote');
        this.paneService.updateHistoryButtonState();
    }

    public confirmNewNote(): Promise<NewNoteAction> {
        return new Promise((resolve) => {
            this.dom.newNoteConfirmOverlay.classList.remove('hidden');

            const saveHandler = () => {
                cleanup();
                resolve('save');
            };
            const discardHandler = () => {
                cleanup();
                resolve('discard');
            };
            const cancelHandler = () => {
                cleanup();
                resolve('cancel');
            };
            const overlayClickHandler = (e: MouseEvent) => {
                if (e.target === this.dom.newNoteConfirmOverlay) {
                    cancelHandler();
                }
            };
            const cleanup = () => {
                this.dom.newNoteConfirmOverlay.classList.add('hidden');
                this.dom.saveAndCreateNewButton.removeEventListener('click', saveHandler);
                this.dom.discardAndCreateNewButton.removeEventListener('click', discardHandler);
                this.dom.newNoteConfirmOverlay.removeEventListener('click', overlayClickHandler);
            };

            this.dom.saveAndCreateNewButton.addEventListener('click', saveHandler, { once: true });
            this.dom.discardAndCreateNewButton.addEventListener('click', discardHandler, { once: true });
            this.dom.newNoteConfirmOverlay.addEventListener('click', overlayClickHandler);
        });
    }

    public handleDeleteNoteClick(note: Note): void {
        this.dom.deleteNoteMessage.textContent = `Are you sure you want to permanently delete "${note.title}"? This action cannot be undone.`;
        this.dom.deleteNoteConfirmOverlay.classList.remove('hidden');

        const confirmHandler = async () => {
            try {
                if (note.driveFileId) {
                    await this.driveService.deleteNoteFromDrive(note.driveFileId);
                }
                await this.dbService.deleteNote(note.id);
                await this.dbService.deleteNoteHistoryForNote(note.id);

                if (this.noteService.currentNote?.id === note.id) {
                    await this.noteService.loadMostRecentNote();
                }
            } catch (e) {
                ErrorHandler.handle('deleting note', e, this.dom);
            } finally {
                cleanup();
                await this.paneService.handleFetchLocalNotes();
            }
        };
        const cancelHandler = () => cleanup();

        const cleanup = () => {
            this.dom.deleteNoteConfirmOverlay.classList.add('hidden');
            this.dom.confirmDeleteNoteButton.removeEventListener('click', confirmHandler);
            this.dom.cancelDeleteNoteButton.removeEventListener('click', cancelHandler);
        };

        this.dom.confirmDeleteNoteButton.addEventListener('click', confirmHandler, { once: true });
        this.dom.cancelDeleteNoteButton.addEventListener('click', cancelHandler, { once: true });
    }
    
    private async performSelectionAction(type: 'polish' | 'rewrite'): Promise<void> {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            this.statusManager.error(`No text selected to ${type}`);
            return;
        }
        this.polishSelectionRange = selection.getRangeAt(0).cloneRange();
        const selectedText = this.polishSelectionRange.toString().trim();
        if (!selectedText) return;

        this.dom.polishSelectionOverlay.classList.remove('hidden');
        this.dom.polishSelectionTitle.textContent = type === 'polish' ? 'Polish Selection' : 'Rewrite Selection';
        this.dom.polishOriginalContent.textContent = selectedText;
        this.dom.polishPolishedContent.innerHTML = '<div class="notes-list-message">Working on it...</div>';
        
        try {
            const fastModel = this.settingsService.getFastModel();
            const rewriteModel = this.settingsService.getRewritePolishingModel();
            const noteId = this.noteService.currentNote?.id ?? null;
            
            // Check if we should detect language or use output language setting
            let targetLanguage: string;
            
            if (this.settingsService.outputLanguage === 'autodetect') {
                const { text: language } = await this.geminiService.detectLanguage(selectedText, fastModel);
                targetLanguage = language;
            } else {
                targetLanguage = this.settingsService.outputLanguage;
            }
            
            const { text: resultText, usageMetadata } = type === 'polish'
                ? await this.geminiService.getPolishedNote(selectedText, targetLanguage, rewriteModel)
                : await this.geminiService.rewriteSelectionForClarity(selectedText, targetLanguage, rewriteModel);

            const operationName = type === 'polish' ? 'Polish Selection' : 'Rewrite Selection';
            this.uiService.addTokenUsageRecord(operationName, usageMetadata, rewriteModel, noteId);
            this.dom.polishPolishedContent.innerHTML = await parseMarkdown(resultText);
            
            const applyHandler = () => {
                if (this.polishSelectionRange) {
                    selection.removeAllRanges();
                    selection.addRange(this.polishSelectionRange);
                    document.execCommand('insertHTML', false, this.dom.polishPolishedContent.innerHTML);
                    this.noteService.setDirty(true);
                    this.editorService.saveState(true);
                }
                cleanup();
            };
            
            const cancelHandler = () => cleanup();
            
            const cleanup = () => {
                this.dom.polishSelectionOverlay.classList.add('hidden');
                this.dom.applyPolishButton.removeEventListener('click', applyHandler);
                this.dom.cancelPolishButton.removeEventListener('click', cancelHandler);
                this.polishSelectionRange = null;
            };

            this.dom.applyPolishButton.addEventListener('click', applyHandler, { once: true });
            this.dom.cancelPolishButton.addEventListener('click', cancelHandler, { once: true });

        } catch (e) {
            ErrorHandler.handle(`${type} operation`, e);
            this.dom.polishPolishedContent.innerHTML = `<div class="notes-list-message">An error occurred. Please try again.</div>`;
        }
    }

    public async showRewriteDialogForFullContent(operationName: string, originalHtml: string, rewriteFn: () => Promise<string>): Promise<void> {
        this.dom.polishSelectionOverlay.classList.remove('hidden');
        this.dom.polishSelectionTitle.textContent = operationName;
        this.dom.polishOriginalContent.innerHTML = originalHtml;
        this.dom.polishPolishedContent.innerHTML = '<div class="notes-list-message">Working on it...</div>';
        
        try {
            const rewrittenHtml = await rewriteFn();
            this.dom.polishPolishedContent.innerHTML = rewrittenHtml;
            
            const applyHandler = () => {
                const activeEditor = this.editorService.getActiveEditor();
                if (activeEditor) {
                    activeEditor.innerHTML = rewrittenHtml; // Replace entire content
                    this.noteService.setDirty(true);
                    this.editorService.saveState(true);
                }
                cleanup();
            };
            
            const cancelHandler = () => cleanup();
            
            const cleanup = () => {
                this.dom.polishSelectionOverlay.classList.add('hidden');
                this.dom.applyPolishButton.removeEventListener('click', applyHandler);
                this.dom.cancelPolishButton.removeEventListener('click', cancelHandler);
            };
    
            this.dom.applyPolishButton.addEventListener('click', applyHandler, { once: true });
            this.dom.cancelPolishButton.addEventListener('click', cancelHandler, { once: true });
    
        } catch (e) {
            ErrorHandler.handle(`${operationName} operation`, e);
            this.dom.polishPolishedContent.innerHTML = `<div class="notes-list-message">An error occurred. Please try again.</div>`;
            // Add a close button or similar for the error case
            const cancelHandler = () => {
                this.dom.polishSelectionOverlay.classList.add('hidden');
                this.dom.cancelPolishButton.removeEventListener('click', cancelHandler);
            };
            this.dom.applyPolishButton.style.display = 'none';
            this.dom.cancelPolishButton.textContent = 'Close';
            this.dom.cancelPolishButton.addEventListener('click', cancelHandler, { once: true });
        }
    }

    public handlePolishSelection(): void {
        this.uiService.closeAllPopovers();
        this.performSelectionAction('polish');
    }

    public handleRewriteSelection(): void {
        this.performSelectionAction('rewrite');
    }

    private async handleSaveLocal(): Promise<void> {
        this.uiService.closeAllPopovers();
        this.editorService.syncActiveContentToModel();
        this.noteService.syncUItoModel(false);
        await this.noteService.saveCurrentNote(true);
        this.statusManager.success('Note saved locally');
    }

    private handleSaveToDrive(): void {
        this.uiService.closeAllPopovers();
        this.editorService.syncActiveContentToModel();
        this.noteService.syncUItoModel(false);

        const currentNote = this.noteService.currentNote;
        if (!currentNote) return;

        let noteTitle = currentNote.title || 'Untitled Note';
        if (this.dom.prependDateToggle.checked) {
            const now = new Date();
            const dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
            const timeString = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
            noteTitle = `${dateString}_${timeString}_${noteTitle}`;
        }

        const category = this.dom.driveCategoryInput.value.trim();
        const tags = this.dom.driveTagsInput.value.trim().split(',').map(t => t.trim()).filter(Boolean);
        currentNote.category = category;
        currentNote.tags = tags;
        this.noteService.refreshNoteMetadataDisplay(); // Update displayed categories and tags

        const frontMatterParts: string[] = [];
        if (category) frontMatterParts.push(`category: ${category}`);
        if (tags.length > 0) frontMatterParts.push(`tags: [${tags.join(', ')}]`);
        frontMatterParts.push(`created: ${new Date(currentNote.timestamp).toISOString()}`);
        const frontMatter = `---\n${frontMatterParts.join('\n')}\n---\n\n`;

        const contentParts: string[] = [];
        if (this.dom.savePolishedToggle.checked && currentNote.polishedNote) contentParts.push(`**Polished Note**\n\n${currentNote.polishedNote}`);
        if (this.dom.saveRawToggle.checked && currentNote.rawTranscription) contentParts.push(`**Raw Transcription**\n\n${currentNote.rawTranscription}`);
        if (this.dom.saveTranslatedToggle.checked && currentNote.translatedNote) contentParts.push(`**Translated Content**\n\n${currentNote.translatedNote}`);

        if (contentParts.length === 0) {
            this.statusManager.error('No selected content to save');
            return;
        }

        const finalContent = `${frontMatter}# ${noteTitle}\n\n${contentParts.join('\n\n---\n\n')}`;
        const saveAction = () => this.performSave(finalContent, noteTitle);
        
        this.authService.requestAuthAndRun(saveAction);
    }
    
    private async performSave(noteContent: string, noteTitle: string): Promise<void> {
        if (!this.noteService.currentNote) return;
        try {
            this.statusManager.loading('Saving to Google Drive');
            this.dom.confirmSaveButton.disabled = true;
            const { webViewLink, fileId } = await this.driveService.saveNote(noteContent, noteTitle, this.noteService.currentNote);
            
            this.noteService.currentNote.driveFileId = fileId;
            await this.noteService.saveCurrentNote(true);
            
            this.statusManager.custom(`Note saved. <a href="${webViewLink}" target="_blank" rel="noopener noreferrer">Open in Drive</a>`);
        } catch (error: any) {
            ErrorHandler.handle('saving to drive', error, this.dom);
            const errorMessage = error.result?.error?.message || 'An unknown error occurred.';
            this.statusManager.custom(`Error saving to Drive: ${errorMessage}`);
            if (error.status === 401 || error.status === 403) {
                (window as any).gapi.client.setToken(null);
                const currentStatus = this.dom.recordingStatus.innerHTML;
                this.statusManager.custom(`${currentStatus} Please try saving again.`);
            }
        } finally {
            this.uiService.updateConfirmSaveButtonState();
        }
    }
}